import React, { useState, useMemo } from "react"
import PropTypes from "prop-types"
import { Spinner } from "reactstrap"
import Select from "react-select"

// Estilos compactos para react-select dentro de la barra de filtros
const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 32,
    minWidth: 240,
    maxWidth: 360,
    fontSize: 13,
    borderColor: state.isFocused ? "#3b82f6" : "#cbd5e1",
    boxShadow: state.isFocused ? "0 0 0 2px rgba(59,130,246,0.15)" : "none",
    "&:hover": { borderColor: "#94a3b8" },
  }),
  valueContainer: base => ({ ...base, padding: "0 8px" }),
  multiValue: base => ({
    ...base,
    background: "#dbeafe",
    borderRadius: 6,
  }),
  multiValueLabel: base => ({ ...base, color: "#1e40af", fontSize: 12, fontWeight: 600 }),
  multiValueRemove: base => ({
    ...base,
    color: "#1e40af",
    "&:hover": { background: "#bfdbfe", color: "#1e3a8a" },
  }),
  indicatorsContainer: base => ({ ...base, height: 32 }),
  menu: base => ({ ...base, zIndex: 50, fontSize: 13 }),
  option: (base, state) => ({
    ...base,
    fontSize: 13,
    padding: "6px 10px",
    background: state.isSelected
      ? "#3b82f6"
      : state.isFocused
        ? "#eff6ff"
        : "white",
    color: state.isSelected ? "white" : "#0f172a",
  }),
}

const ESTADO_BADGE = {
  excelente: { color: "#065f46", bg: "#d1fae5", label: "Excelente" },
  bueno: { color: "#1e40af", bg: "#dbeafe", label: "Bueno" },
  atencion: { color: "#92400e", bg: "#fef3c7", label: "Atención" },
  regular: { color: "#92400e", bg: "#fef3c7", label: "Regular" },
  critico: { color: "#991b1b", bg: "#fee2e2", label: "Crítico" },
}

const formatNumber = n => (typeof n === "number" ? n.toLocaleString("es-CO") : n ?? "—")

const formatDensity = d => {
  if (d === null || d === undefined) return "—"
  const num = typeof d === "string" ? parseFloat(d) : d
  return isNaN(num) ? "—" : `${num.toFixed(2)}%`
}

const Badge = ({ estado }) => {
  const cfg = ESTADO_BADGE[estado] || { color: "#475569", bg: "#f1f5f9", label: estado || "—" }
  return (
    <span
      style={{
        display: "inline-block",
        padding: "3px 10px",
        borderRadius: 999,
        background: cfg.bg,
        color: cfg.color,
        fontSize: 12,
        fontWeight: 600,
      }}
    >
      {cfg.label}
    </span>
  )
}

Badge.propTypes = { estado: PropTypes.string }

const ORDEN_CAMPOS = {
  prefijo: p => (p.prefijo || "").toLowerCase(),
  total_folios: p => p.total_folios ?? 0,
  gaps_estimados: p => p.gaps_estimados ?? 0,
  densidad: p => {
    const d = p.densidad
    return typeof d === "string" ? parseFloat(d) || 0 : d ?? 0
  },
}

const cellSticky = {
  padding: "12px 14px",
  fontSize: 12,
  fontWeight: 600,
  color: "#475569",
  textTransform: "uppercase",
  letterSpacing: 0.4,
  borderBottom: "1px solid #e2e8f0",
  background: "#f1f5f9",
  position: "sticky",
  top: 0,
  zIndex: 1,
}

const HeaderSort = ({ campo, orden, onClick, align = "left", children }) => {
  const activo = orden.campo === campo
  return (
    <th
      onClick={() => onClick(campo)}
      style={{
        ...cellSticky,
        textAlign: align,
        cursor: "pointer",
        userSelect: "none",
        color: activo ? "#0f172a" : "#475569",
      }}
      title="Ordenar"
    >
      {children}
      <i
        className={`mdi mdi-${
          activo ? (orden.asc ? "chevron-up" : "chevron-down") : "unfold-more-horizontal"
        } ms-1`}
        style={{ opacity: activo ? 1 : 0.4, fontSize: 14 }}
      />
    </th>
  )
}

HeaderSort.propTypes = {
  campo: PropTypes.string.isRequired,
  orden: PropTypes.object.isRequired,
  onClick: PropTypes.func.isRequired,
  align: PropTypes.string,
  children: PropTypes.node,
}

const filaPrefijoStyle = (expandido, compartido) => ({
  background: expandido ? "#eff6ff" : compartido ? "#fefce8" : "#fff",
  borderBottom: "1px solid #e2e8f0",
  cursor: "pointer",
  transition: "background 0.15s",
})

const TablaPrefijos = ({
  prefijos,
  sucursalesDisponibles,
  loading,
  onVerDetalle,
  onDescargarCSV,
  exportingPrefijo,
}) => {
  const [busqueda, setBusqueda] = useState("")
  const [expandidas, setExpandidas] = useState(() => new Set())
  const [soloConGaps, setSoloConGaps] = useState(false)
  const [soloCompartidos, setSoloCompartidos] = useState(false)
  const [sucursalesSel, setSucursalesSel] = useState([])
  const [orden, setOrden] = useState({ campo: null, asc: true })

  const sucursalesOptions = useMemo(
    () => (sucursalesDisponibles || []).map(s => ({ value: s, label: s })),
    [sucursalesDisponibles],
  )

  const toggleOrden = campo => {
    setOrden(prev => {
      if (prev.campo !== campo) return { campo, asc: campo === "prefijo" }
      if (prev.asc && campo !== "prefijo") return { campo, asc: false }
      if (!prev.asc) return { campo: null, asc: true }
      return { campo, asc: !prev.asc }
    })
  }

  const filtrados = useMemo(() => {
    if (!prefijos) return []
    const q = busqueda.toLowerCase()
    const sucursalesFiltro = sucursalesSel.length > 0 ? new Set(sucursalesSel.map(s => s.value)) : null
    const base = prefijos
      .filter(p => (soloConGaps ? (p.gaps_estimados ?? 0) > 0 : true))
      .filter(p => (soloCompartidos ? p.es_compartido : true))
      .filter(p => {
        if (!sucursalesFiltro) return true
        return (p.sucursales_lista || []).some(s => sucursalesFiltro.has(s))
      })
      .filter(p => {
        if (!q) return true
        if ((p.prefijo || "").toLowerCase().includes(q)) return true
        if ((p.etiqueta_visual || "").toLowerCase().includes(q)) return true
        return (p.sucursales_lista || []).some(s => (s || "").toLowerCase().includes(q))
      })

    if (!orden.campo) return base
    const getter = ORDEN_CAMPOS[orden.campo]
    if (!getter) return base
    return [...base].sort((a, b) => {
      const av = getter(a)
      const bv = getter(b)
      if (typeof av === "string") return orden.asc ? av.localeCompare(bv) : bv.localeCompare(av)
      return orden.asc ? av - bv : bv - av
    })
  }, [prefijos, busqueda, soloConGaps, soloCompartidos, sucursalesSel, orden])

  const toggleExpandida = prefijo => {
    setExpandidas(prev => {
      const next = new Set(prev)
      if (next.has(prefijo)) next.delete(prefijo)
      else next.add(prefijo)
      return next
    })
  }

  const expandirTodas = () => setExpandidas(new Set(filtrados.map(p => p.prefijo)))
  const colapsarTodas = () => setExpandidas(new Set())

  const expandidasEffective = useMemo(() => {
    if (busqueda) return new Set(filtrados.map(p => p.prefijo))
    return expandidas
  }, [busqueda, filtrados, expandidas])

  if (loading) {
    return (
      <div style={{ padding: 60, textAlign: "center", color: "#64748b" }}>
        <Spinner color="primary" />
        <div className="mt-2">Cargando prefijos…</div>
      </div>
    )
  }

  if (!prefijos || prefijos.length === 0) {
    return (
      <div style={{ padding: 60, textAlign: "center", color: "#64748b" }}>
        <i className="mdi mdi-tag-off-outline" style={{ fontSize: 48, color: "#cbd5e1" }} />
        <div className="mt-2">Sin prefijos para los filtros aplicados.</div>
      </div>
    )
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <h6 className="mb-0" style={{ color: "#0f172a", fontWeight: 600 }}>
          Prefijos ({filtrados.length})
        </h6>
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <div style={{ position: "relative", maxWidth: 260 }}>
            <i
              className="mdi mdi-magnify"
              style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#94a3b8", zIndex: 1 }}
            />
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="Buscar prefijo o sucursal…"
              value={busqueda}
              onChange={e => setBusqueda(e.target.value)}
              style={{ paddingLeft: 32, minWidth: 220 }}
            />
          </div>
          <Select
            isMulti
            isClearable
            isSearchable
            closeMenuOnSelect={false}
            placeholder={
              <span style={{ color: "#94a3b8" }}>
                <i className="mdi mdi-source-branch me-1" />
                Filtrar por sucursales…
              </span>
            }
            noOptionsMessage={() => "Sin sucursales"}
            options={sucursalesOptions}
            value={sucursalesSel}
            onChange={v => setSucursalesSel(v || [])}
            styles={selectStyles}
            menuPortalTarget={typeof document !== "undefined" ? document.body : null}
            menuPosition="fixed"
          />
          <div className="form-check form-switch m-0">
            <input
              type="checkbox"
              className="form-check-input"
              role="switch"
              id="soloConGaps"
              checked={soloConGaps}
              onChange={e => setSoloConGaps(e.target.checked)}
            />
            <label className="form-check-label small" htmlFor="soloConGaps" style={{ color: "#475569" }}>
              Solo con gaps
            </label>
          </div>
          <div className="form-check form-switch m-0">
            <input
              type="checkbox"
              className="form-check-input"
              role="switch"
              id="soloCompartidos"
              checked={soloCompartidos}
              onChange={e => setSoloCompartidos(e.target.checked)}
            />
            <label className="form-check-label small" htmlFor="soloCompartidos" style={{ color: "#475569" }}>
              Solo compartidos
            </label>
          </div>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={expandidasEffective.size === filtrados.length ? colapsarTodas : expandirTodas}
            disabled={Boolean(busqueda)}
            title={busqueda ? "Auto-expandido por búsqueda" : ""}
          >
            <i className={`mdi mdi-${expandidasEffective.size === filtrados.length ? "collapse-all" : "expand-all"} me-1`} />
            {expandidasEffective.size === filtrados.length ? "Colapsar" : "Expandir"} todo
          </button>
        </div>
      </div>

      <div
        style={{
          border: "1px solid #e2e8f0",
          borderRadius: 12,
          overflow: "hidden",
          background: "#fff",
        }}
      >
        <div style={{ maxHeight: 580, overflowY: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={{ ...cellSticky, width: 40 }} />
                <HeaderSort campo="prefijo" orden={orden} onClick={toggleOrden}>
                  Prefijo
                </HeaderSort>
                <HeaderSort campo="total_folios" orden={orden} onClick={toggleOrden} align="right">
                  Folios
                </HeaderSort>
                <HeaderSort campo="gaps_estimados" orden={orden} onClick={toggleOrden} align="right">
                  Gaps
                </HeaderSort>
                <HeaderSort campo="densidad" orden={orden} onClick={toggleOrden} align="right">
                  Densidad
                </HeaderSort>
                <th style={cellSticky}>Estado</th>
                <th style={{ ...cellSticky, textAlign: "right" }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filtrados.map(pref => {
                const expandido = expandidasEffective.has(pref.prefijo)
                const gaps = pref.gaps_estimados ?? 0
                const exportando = exportingPrefijo === pref.prefijo
                const sucursales = pref.sucursales_lista || []
                const compartido = Boolean(pref.es_compartido)

                return (
                  <React.Fragment key={pref.prefijo}>
                    <tr style={filaPrefijoStyle(expandido, compartido)} onClick={() => toggleExpandida(pref.prefijo)}>
                      <td style={{ padding: "12px 14px", textAlign: "center", color: "#64748b" }}>
                        <i
                          className={`mdi mdi-chevron-${expandido ? "down" : "right"}`}
                          style={{ fontSize: 18 }}
                        />
                      </td>
                      <td style={{ padding: "12px 14px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                          <span
                            style={{
                              fontWeight: 700,
                              color: "#0f172a",
                              fontFamily: "ui-monospace, SFMono-Regular, monospace",
                              fontSize: 14,
                            }}
                          >
                            <i className="mdi mdi-tag-outline text-primary me-2" />
                            {pref.prefijo}
                          </span>
                          {compartido && (
                            <span
                              style={{
                                padding: "2px 8px",
                                borderRadius: 999,
                                background: "#fef3c7",
                                color: "#92400e",
                                fontSize: 11,
                                fontWeight: 700,
                                textTransform: "uppercase",
                                letterSpacing: 0.4,
                              }}
                              title="Este prefijo es usado por más de una sucursal"
                            >
                              <i className="mdi mdi-source-branch me-1" />
                              Compartido
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>
                          {sucursales.length === 0
                            ? "—"
                            : sucursales.length === 1
                              ? sucursales[0]
                              : `${sucursales.length} sucursales: ${sucursales.join(" + ")}`}
                        </div>
                      </td>
                      <td style={{ padding: "12px 14px", textAlign: "right", color: "#475569", fontWeight: 600 }}>
                        {formatNumber(pref.total_folios)}
                      </td>
                      <td style={{ padding: "12px 14px", textAlign: "right" }}>
                        <span
                          style={{
                            fontWeight: 700,
                            color: gaps > 0 ? "#dc2626" : "#10b981",
                          }}
                        >
                          {formatNumber(gaps)}
                        </span>
                      </td>
                      <td style={{ padding: "12px 14px", textAlign: "right", color: "#475569" }}>
                        {formatDensity(pref.densidad)}
                      </td>
                      <td style={{ padding: "12px 14px" }}>
                        <Badge estado={pref.estado} />
                      </td>
                      <td
                        style={{ padding: "10px 14px", textAlign: "right", whiteSpace: "nowrap" }}
                        onClick={e => e.stopPropagation()}
                      >
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary me-1"
                          onClick={() => onVerDetalle(pref)}
                          title="Ver gaps, historial e incremental"
                        >
                          <i className="mdi mdi-eye-outline me-1" />
                          Ver
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-success"
                          onClick={() => onDescargarCSV(pref)}
                          disabled={gaps === 0 || exportando}
                          title={gaps === 0 ? "Sin gaps para exportar" : "Descargar CSV"}
                        >
                          {exportando ? (
                            <Spinner size="sm" />
                          ) : (
                            <>
                              <i className="mdi mdi-file-download-outline me-1" />
                              CSV
                            </>
                          )}
                        </button>
                      </td>
                    </tr>

                    {expandido && (
                      <tr style={{ background: "#fafafa" }}>
                        <td />
                        <td colSpan={6} style={{ padding: "16px 20px" }}>
                          <div
                            style={{
                              fontSize: 12,
                              color: "#64748b",
                              marginBottom: 10,
                              textTransform: "uppercase",
                              fontWeight: 600,
                              letterSpacing: 0.4,
                            }}
                          >
                            <i className="mdi mdi-source-branch me-1" />
                            Sucursales participantes ({pref.sucursales_participantes?.length || 0})
                          </div>

                          {compartido && gaps > 0 && (
                            <div
                              style={{
                                background: "#fffbeb",
                                border: "1px solid #fde68a",
                                borderRadius: 8,
                                padding: "8px 12px",
                                marginBottom: 10,
                                fontSize: 12,
                                color: "#92400e",
                              }}
                            >
                              <i className="mdi mdi-information-outline me-1" />
                              Los <strong>{formatNumber(gaps)} gaps</strong> son del prefijo en su totalidad —
                              no se atribuyen a una sucursal específica.
                            </div>
                          )}

                          <table style={{ width: "100%", borderCollapse: "collapse" }}>
                            <thead>
                              <tr style={{ background: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
                                <th style={{ padding: "8px 12px", fontSize: 11, fontWeight: 600, color: "#64748b", textAlign: "left", textTransform: "uppercase", letterSpacing: 0.4 }}>Sucursal</th>
                                <th style={{ padding: "8px 12px", fontSize: 11, fontWeight: 600, color: "#64748b", textAlign: "right", textTransform: "uppercase", letterSpacing: 0.4 }}>Folios</th>
                                <th style={{ padding: "8px 12px", fontSize: 11, fontWeight: 600, color: "#64748b", textAlign: "right", textTransform: "uppercase", letterSpacing: 0.4 }}>Folio min</th>
                                <th style={{ padding: "8px 12px", fontSize: 11, fontWeight: 600, color: "#64748b", textAlign: "right", textTransform: "uppercase", letterSpacing: 0.4 }}>Folio máx</th>
                                <th style={{ padding: "8px 12px", fontSize: 11, fontWeight: 600, color: "#64748b", textAlign: "right", textTransform: "uppercase", letterSpacing: 0.4 }}>%</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(pref.sucursales_participantes || []).map(sp => (
                                <tr key={sp.sucursal} style={{ borderBottom: "1px solid #f1f5f9" }}>
                                  <td style={{ padding: "8px 12px", color: "#0f172a", fontSize: 13, fontWeight: 500 }}>{sp.sucursal}</td>
                                  <td style={{ padding: "8px 12px", textAlign: "right", color: "#475569", fontSize: 13 }}>{formatNumber(sp.folios_emitidos)}</td>
                                  <td style={{ padding: "8px 12px", textAlign: "right", color: "#475569", fontFamily: "ui-monospace, SFMono-Regular, monospace", fontSize: 13 }}>{formatNumber(sp.folio_min)}</td>
                                  <td style={{ padding: "8px 12px", textAlign: "right", color: "#475569", fontFamily: "ui-monospace, SFMono-Regular, monospace", fontSize: 13 }}>{formatNumber(sp.folio_max)}</td>
                                  <td style={{ padding: "8px 12px", textAlign: "right", color: "#0f172a", fontWeight: 600, fontSize: 13 }}>
                                    {typeof sp.porcentaje === "number" ? `${sp.porcentaje.toFixed(2)}%` : "—"}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

TablaPrefijos.propTypes = {
  prefijos: PropTypes.array,
  sucursalesDisponibles: PropTypes.arrayOf(PropTypes.string),
  loading: PropTypes.bool,
  onVerDetalle: PropTypes.func.isRequired,
  onDescargarCSV: PropTypes.func.isRequired,
  exportingPrefijo: PropTypes.string,
}

export default TablaPrefijos
